<script setup lang="ts">
import type { MediaItem } from "@/types/album";

import MediaCard from "@/components/gallery/MediaCard.vue";

/**
 * Port de `.gallery` (design-reference/styles.css) : mise en page en
 * `columns`, 2 sur mobile / 4 sur bureau.
 *
 * Les rotations `nth-child` de la maquette portent sur la position de
 * chaque polaroïd dans cette grille : elles sont donc déclarées ici plutôt
 * que dans `MediaCard.vue`. Comme le nœud racine d'un composant enfant
 * appartient aussi à la portée `scoped` du parent qui le rend, le sélecteur
 * `.gallery .media-card` cible bien la racine de chaque `MediaCard` sans
 * `:deep()`.
 */
withDefaults(defineProps<{ items: MediaItem[]; highlightedId?: string | null }>(), {
  highlightedId: null,
});
const emit = defineEmits<{ select: [id: string] }>();
</script>

<template>
  <!-- Panneau cible des onglets de filtre : l'identifiant doit rester stable,
       d'où ce conteneur qui englobe la mosaïque comme l'état vide. -->
  <div id="album-gallery" role="tabpanel" tabindex="-1">
    <div v-if="items.length > 0" class="gallery" data-testid="gallery">
      <MediaCard
        v-for="item in items"
        :key="item.id"
        :item="item"
        :highlighted-id="highlightedId"
        @select="emit('select', $event)"
      />
    </div>
    <div v-else class="gallery-empty" data-testid="gallery-empty">
      <span class="gallery-empty__mark" aria-hidden="true">✦</span>
      <p class="gallery-empty__title">Aucun souvenir pour ce moment.</p>
      <p class="gallery-empty__hint">
        Personne n’a encore rien déposé ici. Changez de filtre, ou ajoutez la première photo de ce
        moment.
      </p>
    </div>
  </div>
</template>

<style scoped>
.gallery {
  columns: 2;
  column-gap: 13px;
  max-width: 1120px;
  margin: auto;
  padding: 12px 3px;
}

.gallery .media-card {
  transform: rotate(-1.2deg);
  transform-origin: center;
}

.gallery .media-card:nth-child(2n) {
  transform: rotate(1.4deg);
}

.gallery .media-card:nth-child(3n) {
  transform: rotate(-2deg);
}

.gallery .media-card:nth-child(5n) {
  transform: rotate(2.1deg);
}

.gallery .media-card:hover,
.gallery .media-card:focus-within {
  transform: rotate(0) translateY(-5px) scale(1.015);
}

.gallery-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 420px;
  margin: 60px auto;
  padding: 40px 20px;
  text-align: center;
}

.gallery-empty__mark {
  color: var(--color-rose);
  font-size: 20px;
}

.gallery-empty__title {
  margin: 0;
  color: var(--color-ink);
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 400;
  -webkit-text-stroke: 0.12px currentColor;
}

.gallery-empty__hint {
  margin: 0;
  color: var(--color-muted);
  font-size: 13px;
  line-height: 1.6;
}

@media (min-width: 760px) {
  .gallery {
    columns: 4;
    column-gap: 24px;
    padding: 18px 12px;
  }
}
</style>
